import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
// import UpdateCouponModal from "components/Modals/UpdateCouponModal";
import { couponApiService } from "services/couponApiService";
import DeleteModal from "pages/Calendar/DeleteModal";
import UpdateCouponModal from "./UpdateCouponModal";

const AllCoupons = () => {
  const navigate = useNavigate();

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState(null);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [deletingCoupon, setDeletingCoupon] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Fetch coupons
  const fetchCoupons = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await couponApiService.getAllCoupons();
      setCoupons(data);
    } catch (error) {
      toast.error("Failed to fetch coupons");
      console.error("Error fetching coupons:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const showDeleteModal = (id) => {
    setSelectedCouponId(id);
    setDeleteModal(true);
  };

  const handleDeleteCoupon = async () => {
    setDeletingCoupon(true);
    try {
      await couponApiService.deleteCoupon(selectedCouponId);
      toast.success("Coupon deleted successfully");
      setDeleteModal(false);
      fetchCoupons(false);
    } catch (error) {
      toast.error("Failed to delete coupon");
      console.error("Error deleting coupon:", error);
    } finally {
      setDeletingCoupon(false);
    }
  };

  const handleUpdateCoupon = (coupon) => {
    setSelectedCoupon(coupon);
    setUpdateModalOpen(true);
  };

  const handleUpdateSuccess = () => {
    fetchCoupons(false);
    toast.success("Coupon updated successfully");
  };

  // Filter coupons based on search and status
  const filteredCoupons = useMemo(() => {
    let filtered = coupons;

    // Apply status filter
    if (statusFilter === "active") {
      filtered = filtered.filter(coupon => coupon.activestatus === true);
    } else if (statusFilter === "inactive") {
      filtered = filtered.filter(coupon => coupon.activestatus === false);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (coupon) =>
          (coupon.id && coupon.id.toString().toLowerCase().includes(query)) ||
          (coupon.pro_name && coupon.pro_name.toLowerCase().includes(query)) ||
          (coupon.promocode && coupon.promocode.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [coupons, statusFilter, searchQuery]);

  const formatCurrency = (amount) => {
    if (!amount) return "$0";
    return `$${parseFloat(amount).toLocaleString('en-US')}`;
  };

  const getStatusBadge = (status) => {
    return status ? (
      <span className="badge bg-success">Active</span>
    ) : (
      <span className="badge bg-danger">Inactive</span>
    );
  };

  const getBooleanBadge = (value) => {
    return value ? (
      <span className="badge bg-success">Yes</span>
    ) : (
      <span className="badge bg-warning">No</span>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            #{row.original.id}
          </div>
        ),
      },
      {
        header: "Promo Name",
        accessorKey: "pro_name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {row.original.pro_name || "-"}
          </div>
        ),
      },
      {
        header: "Promo Code",
        accessorKey: "promocode",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className=" text-primary">
            {row.original.promocode || "-"}
          </div>
        ),
      },
      {
        header: "Percentage",
        accessorKey: "promo_percentage",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {row.original.promo_percentage ? `${row.original.promo_percentage}%` : "-"}
          </div>
        ),
      },
      {
        header: "Fixed Amount",
        accessorKey: "promo_fixedamount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {row.original.promo_fixedamount ? formatCurrency(row.original.promo_fixedamount) : "-"}
          </div>
        ),
      },
      {
        header: "Free Delivery",
        accessorKey: "is_freedelivery",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => getBooleanBadge(row.original.is_freedelivery),
      },
      {
        header: "Is Percentage",
        accessorKey: "is_percentage",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => getBooleanBadge(row.original.is_percentage),
      },
      {
        header: "Is Fixed Amount",
        accessorKey: "is_fixedamount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => getBooleanBadge(row.original.is_fixedamount),
      },
      {
        header: "Status",
        accessorKey: "activestatus",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => getStatusBadge(row.original.activestatus),
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              onClick={() => handleUpdateCoupon(row.original)}
              className="btn btn-warning btn-sm"
              title="Edit Coupon"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => showDeleteModal(row.original.id)}
              title="Delete Coupon"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // Calculate statistics
  const totalCoupons = coupons.length;
  const activeCoupons = coupons.filter(coupon => coupon.activestatus === true).length;
  const percentageCoupons = coupons.filter(coupon => coupon.is_percentage === true).length;
  const freeDeliveryCoupons = coupons.filter(coupon => coupon.is_freedelivery === true).length;

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Coupons" breadcrumbItem="All Coupons" />

        {/* Statistics Cards */}
        {/* <div className="row mb-4">
          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Total Coupons</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{totalCoupons}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-primary rounded-circle">
                        <i className="fas fa-tag font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Active Coupons</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{activeCoupons}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-success rounded-circle">
                        <i className="fas fa-check-circle font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Percentage Coupons</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{percentageCoupons}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-info rounded-circle">
                        <i className="fas fa-percent font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Free Delivery</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{freeDeliveryCoupons}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-warning rounded-circle">
                        <i className="fas fa-truck font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Filters and Actions */}
        <div className="row mb-3">
          <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3 align-items-center">


                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name, promo code..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ width: "250px" }}
                  />
                </div>

                <div className="d-flex align-items-center gap-2">
                  {/* <label className="form-label mb-0">Status:</label> */}
                  <select
                    className="form-select"
                    style={{ width: "auto" }}
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {(statusFilter || searchQuery) && (
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setStatusFilter("");
                      setSearchQuery("");
                    }}
                  >
                    <i className="fas fa-times me-1"></i> Clear Filters
                  </button>
                )}
              </div>

              <div className="d-flex gap-2">
                <button
                  onClick={() => navigate("/create-coupon")}
                  className="btn btn-success"
                >
                  <i className="fas fa-plus me-1"></i> Create New Coupon
                </button>

                <button
                  className="btn btn-light"
                  onClick={() => fetchCoupons(false)}
                  disabled={refreshing}
                  title="Refresh Coupons"
                >
                  <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Coupons Table */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="mb-3">
                  <h5 className="card-title mb-0">
                    Showing {filteredCoupons.length} of {totalCoupons} coupons
                  </h5>
                </div>

                <TableContainer
                  columns={columns}
                  data={filteredCoupons}
                  isGlobalFilter={false}
                  isPagination={true}
                  pagination="pagination"
                  paginationWrapper="dataTables_paginate paging_simple_numbers"
                  tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                  isCustomPageSize={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteCoupon}
          loader={deletingCoupon}
          onCloseClick={() => setDeleteModal(false)}
          title="Delete Coupon"
          message="Are you sure you want to delete this coupon? This action cannot be undone."
        />

        {selectedCoupon && (
          <UpdateCouponModal
            isOpen={updateModalOpen}
            toggle={() => setUpdateModalOpen(false)}
            coupon={selectedCoupon}
            onUpdateSuccess={handleUpdateSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default AllCoupons;